import { loadStripe } from '@stripe/stripe-js';
import { getCurrentInstanceSlug } from './tenant';
import { setCurrency } from './currency';

// Prices can render before the first getConfiguration() resolves — start from the cached
// configuration's currency rather than the default.
try {
    setCurrency(JSON.parse(localStorage.getItem('ticketing_configuration') || 'null')?.currency);
} catch (e) {
    // no or unreadable cache — the default stands until the configuration loads
}

// Tenants that connected via Stripe Connect are scoped with the platform's own
// publishable key + their connected account id; manual-key tenants use their own
// publishable key directly. Both are exposed on the configuration response.
export const loadStripeForConfig = (config) => {
    if (config?.stripe_connect_account_id && config?.stripe_connect_publishable_key) {
        return loadStripe(config.stripe_connect_publishable_key, {
            stripeAccount: config.stripe_connect_account_id,
        });
    }
    return loadStripe(config?.stripe_api_key);
}

let PORTAL_API_URL = '{{ settings.PORTAL_API_URL }}';

export const globalSettings = {
    PORTAL_API_URL: PORTAL_API_URL === '{{ settings.PORTAL_API_URL }}' ? 'https://api.selwynevents.com' : PORTAL_API_URL,
}

globalSettings['PORTAL_API_URL'] = process.env.REACT_APP_PORTAL_API_URL || 'https://api.selwynevents.com';

let cachedConfig = null;
let cacheTimestamp = null;

export const getConfiguration = async () => {
    const now = new Date().getTime();

    if (cachedConfig && cacheTimestamp && (now - cacheTimestamp < 120000)) {
        setCurrency(cachedConfig?.currency);
        return cachedConfig;
    }

    const cachedConfigString = localStorage.getItem('ticketing_configuration');
    const cachedTimestampString = localStorage.getItem('ticketing_configurationTimestamp');

    if (cachedConfigString && cachedTimestampString) {
        const cachedTimestamp = parseInt(cachedTimestampString, 10);
        if (now - cachedTimestamp < 120000) {
            cachedConfig = JSON.parse(cachedConfigString);
            cacheTimestamp = cachedTimestamp;
            setCurrency(cachedConfig?.currency);
            return cachedConfig;
        }
    }

    const url = `${globalSettings.PORTAL_API_URL}/manager/configuration`;

    try {
        const headers = { 'X-Source-Host': window.location.host };
        const slug = getCurrentInstanceSlug();
        if (slug) headers['X-Instance-Slug'] = slug;
        const config = await fetch(url, { headers });
        // A transient backend failure (e.g. the license manager being briefly unreachable)
        // still returns a JSON body (`{"error": "..."}`) with a non-2xx status — .json()
        // parses it fine, so without this check that error response was cached as a valid
        // configuration for 2 minutes, silently disabling ticketing (TicketingEnabledGuard
        // reads capabilities.ticketing_enabled from here) well past the outage itself.
        if (!config.ok) throw new Error(`configuration request failed with status ${config.status}`);
        const config_struct = await config.json();
        cachedConfig = config_struct;
        cacheTimestamp = now;
        localStorage.setItem('ticketing_configuration', JSON.stringify(config_struct));
        localStorage.setItem('ticketing_configurationTimestamp', now.toString());
        setCurrency(config_struct?.currency);
        return config_struct;
    } catch (e) {
        return null;
    }
}

let cachedLanguageSettings = null;
let languageSettingsCacheTimestamp = null;

export const getTicketingLanguageSettings = async () => {
    const now = new Date().getTime();

    if (cachedLanguageSettings && languageSettingsCacheTimestamp && (now - languageSettingsCacheTimestamp < 120000)) {
        return cachedLanguageSettings;
    }

    const cachedString = localStorage.getItem('ticketing_languageSettings');
    const cachedTimestampString = localStorage.getItem('ticketing_languageSettingsTimestamp');

    if (cachedString && cachedTimestampString) {
        const cachedTimestamp = parseInt(cachedTimestampString, 10);
        if (now - cachedTimestamp < 120000) {
            cachedLanguageSettings = JSON.parse(cachedString);
            languageSettingsCacheTimestamp = cachedTimestamp;
            return cachedLanguageSettings;
        }
    }

    const url = `${globalSettings.PORTAL_API_URL}/api/v1/ticketing-language-settings`;

    try {
        const headers = { 'X-Source-Host': window.location.host };
        const slug = getCurrentInstanceSlug();
        if (slug) headers['X-Instance-Slug'] = slug;
        const response = await fetch(url, { headers });
        // See the matching check in getConfiguration() above — an error response is still
        // valid JSON, so without this it would get cached as real settings for 2 minutes.
        if (!response.ok) throw new Error(`ticketing-language-settings request failed with status ${response.status}`);
        const settings = await response.json();
        cachedLanguageSettings = settings;
        languageSettingsCacheTimestamp = now;
        localStorage.setItem('ticketing_languageSettings', JSON.stringify(settings));
        localStorage.setItem('ticketing_languageSettingsTimestamp', now.toString());
        return settings;
    } catch (e) {
        return null;
    }
}

export const customStyles = async () => {
    let colour_off_white_background = '#d8d8d8';
    let colour_menu_main = '#481b52';
    let colour_menu_dark = '#481b52';
    let colour_menu_light = '#70407c';
    let colour_heading_success = '#a4cf58';
    let colour_heading_hover = '#729437';
    let colour_cancel = '#961818';
    let colour_cancel_hover = '#710f0f';
    let colour_grey = '#232323';
    let colour_waiting_header = '#3d3d3d';
    let colour_accent = '#3498db';
    let colour_link = '#29C6B7';
    let custom_css = '';

    const configuration = await getConfiguration();
    if (configuration) {
        colour_off_white_background = configuration.colour_off_white_background || colour_off_white_background;
        colour_menu_main = configuration.colour_menu_main || colour_menu_main;
        colour_menu_dark = configuration.colour_menu_dark || colour_menu_dark;
        colour_menu_light = configuration.colour_menu_light || colour_menu_light;
        colour_heading_success = configuration.colour_heading_success || colour_heading_success;
        colour_heading_hover = configuration.colour_heading_hover || colour_heading_hover;
        colour_cancel = configuration.colour_cancel || colour_cancel;
        colour_cancel_hover = configuration.colour_cancel_hover || colour_cancel_hover;
        colour_grey = configuration.colour_grey || colour_grey;
        colour_waiting_header = configuration.colour_waiting_header || colour_waiting_header;
        colour_accent = configuration.colour_accent || colour_accent;
        colour_link = configuration.colour_link || colour_link;
        custom_css = configuration.custom_css || '';
    }

    const r = document.querySelector(':root');
    r.setAttribute('style', custom_css);
    r.style.setProperty('--colour_off_white_background', colour_off_white_background);
    r.style.setProperty('--colour_menu_main', colour_menu_main);
    r.style.setProperty('--colour_menu_dark', colour_menu_dark);
    r.style.setProperty('--colour_menu_light', colour_menu_light);
    r.style.setProperty('--colour_heading_success', colour_heading_success);
    r.style.setProperty('--colour_heading_hover', colour_heading_hover);
    r.style.setProperty('--colour_cancel', colour_cancel);
    r.style.setProperty('--colour_cancel_hover', colour_cancel_hover);
    r.style.setProperty('--colour_grey', colour_grey);
    r.style.setProperty('--colour_waiting_header', colour_waiting_header);
    r.style.setProperty('--colour_accent', colour_accent);
    r.style.setProperty('--colour_link', colour_link);
}

export const customJavascript = async () => {
    const configuration = await getConfiguration();
    if (configuration?.custom_js) {
        // eslint-disable-next-line
        eval(configuration.custom_js);
    }
}
